import { forwardRef } from 'react'
import type { ReactNode, SelectHTMLAttributes } from 'react'
import { DownOutlined } from '../icons'
import { cx } from './cx'
import { FieldLabel, FieldMessages, useFieldIds } from './fieldChrome'

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size' | 'aria-describedby'> {
  readonly label: string
  /** Visually hide the label (still exposed to assistive tech) — for dense
   * inline controls (e.g. a per-row grant-role select) where a visible label
   * would be redundant with the surrounding column header. Never drop the
   * label entirely: a bare `<select>` with no accessible name is the exact
   * a11y review comment this primitive exists to prevent. */
  readonly hideLabel?: boolean
  /** Control height. `md` (default, 32px) matches the Input primitive for
   * standalone form fields; `sm` (24px) is the compact variant for dense
   * contexts like an in-table per-row select, where the full-height control
   * reads too tall against the row. */
  readonly size?: 'sm' | 'md'
  /** Inline error message — red border + message + `aria-invalid`, mirroring
   * the Input primitive. Presence alone drives the error visual/ARIA state. */
  readonly error?: string | undefined
  readonly helpText?: string | undefined
  /** Visual `*` + `aria-required` + "(required)" in the accessible label —
   * never the asterisk glyph alone (accessibility-wcag skill). */
  readonly required?: boolean
  /** The `<option>` / `<optgroup>` elements. Callers author these directly so
   * bespoke option logic (sentinel values, disabled placeholders, grouping)
   * is preserved unchanged from the native `<select>` being replaced. */
  readonly children: ReactNode
}

/**
 * Select primitive (UID-229) — a design-system-styled NATIVE `<select>`, not a
 * custom listbox. The design source (components.md "Select / dropdown") keeps
 * the native control ("native `<select>` in source — use a styled native
 * select") because native is the most robust for keyboard, mobile, and screen
 * readers; this primitive only restyles it (custom chevron, token border,
 * focus ring, sizing) and standardizes the label / required / error / help
 * markup + ARIA that the ~10 hand-rolled `<select>`s across the app each did
 * differently (or omitted).
 *
 * Mirrors the Input primitive's field structure and forwards its ref to the
 * underlying `<select>` so the same submit-with-errors focus management works.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    hideLabel = false,
    size = 'md',
    error,
    helpText,
    required,
    className,
    children,
    ...rest
  },
  ref,
) {
  const { id: selectId, errorId, helpId, describedBy } = useFieldIds(rest.id, { error, helpText })

  return (
    <div className={cx('field', className)}>
      <FieldLabel htmlFor={selectId} hideLabel={hideLabel} required={required}>
        {label}
      </FieldLabel>
      <div className="select-control-wrap">
        <select
          {...rest}
          ref={ref}
          id={selectId}
          className={cx(
            'select-control',
            size === 'sm' && 'select-control-sm',
            error && 'select-control-error',
          )}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          aria-required={required || undefined}
        >
          {children}
        </select>
        <DownOutlined className="select-chevron" aria-hidden="true" />
      </div>
      <FieldMessages ids={{ errorId, helpId }} error={error} helpText={helpText} />
    </div>
  )
})
